import React from "react";
import { Card } from "reactstrap";
import { CheckIcon } from "@aureskonnect/react-ui";
import { IoMdStar } from "react-icons/io";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import "./index.css";

type PosCardProps = {
    title: string;
    subtitle?: string;
    active: boolean;
    icon?: React.ReactNode;
    isFavorite?: boolean;
    onToggle: () => void;
    disabled?: boolean;
    /**
     * Visual flag for items deactivated in general settings: greys the
     * card to alert the user (mirrors the kiosk pattern). Independent of
     * `disabled` so we keep the existing favorite-active lock visually
     * neutral. Setting this also implies the card is uninteractable:
     * `aria-disabled`/`tabIndex` are updated and clicks are ignored —
     * but no extra CSS class is added (the visual rendering is owned by
     * the existing `pos-card-greyed__clz` wrapper).
     */
    greyedOut?: boolean;
    "data-testid"?: string;
};

export function PosCard({
    title,
    subtitle,
    active,
    icon,
    isFavorite = false,
    onToggle,
    disabled = false,
    greyedOut = false,
    "data-testid": dataTestId,
}: PosCardProps): JSX.Element {
    const { t } = useTranslation();
    const isInert = disabled || greyedOut;
    const ariaLabel = greyedOut
        ? `${title} — ${t("Disabled in general settings")}`
        : title;
    return (
        <div
            className={classNames(
                "d-flex flex-column justify-content-center cursor__clz pos-card-wrapper__clz",
                { "pos-card-wrapper-inactive__clz": !active },
                { "pos-card-greyed__clz": greyedOut }
            )}
        >
            <Card
                className={classNames(
                    "pos-card__clz",
                    active ? "pos-card-active-border__clz" : "pos-card-inactive-border__clz",
                    disabled ? "pos-card-disabled__clz" : "pos-card-enabled__clz"
                )}
                data-testid={dataTestId}
                role="button"
                tabIndex={isInert ? -1 : 0}
                aria-pressed={active ? "true" : "false"}
                aria-label={ariaLabel}
                aria-disabled={isInert ? "true" : "false"}
                onClick={() => {
                    if (!isInert) onToggle();
                }}
                onKeyDown={(e) => {
                    if (!isInert && (e.key === "Enter" || e.key === " ")) {
                        e.preventDefault();
                        onToggle();
                    }
                }}
            >
                <CheckIcon
                    className={classNames(
                        "mt-1 ml-1 pos-card-icon__clz pos-card-check__clz",
                        active ? "pos-card-check-active__clz" : "pos-card-check-inactive__clz",
                        disabled ? "pos-card-disabled__clz" : "pos-card-enabled__clz"
                    )}
                    data-testid={dataTestId ? `${dataTestId}-check` : undefined}
                />
                {isFavorite && (
                    <IoMdStar
                        size={24}
                        className="pos-card-favorite__clz"
                    />
                )}
                <div
                    className={classNames(
                        "d-flex justify-content-center align-items-center pos-card-content__clz",
                        active ? "pos-card-content-active__clz" : "pos-card-content-inactive__clz"
                    )}
                >
                    {icon || (
                        <span className="pos-card-fallback-letter__clz">
                            {title.charAt(0).toUpperCase()}
                        </span>
                    )}
                </div>
            </Card>
            <div className="d-flex flex-column align-items-center">
                <span className="pos-card-title__clz">
                    {title}
                </span>
                {subtitle && (
                    <span className="pos-card-subtitle__clz">
                        {subtitle}
                    </span>
                )}
            </div>
        </div>
    );
}
